import { zodResolver } from '@hookform/resolvers/zod';
import { CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { canUse, useCallerPermissions } from '@/clinical-study/useCallerPermissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';
import { readErrorKey } from '@/lib/read-error';
import { usePendingWrite } from '@/lib/use-pending-write';

type ClinicalStudy =
  paths['/v1/clinical-study/studies']['get']['responses'][200]['content']['application/json'][number];

type LoadState =
  | { status: 'loading'; studies: [] }
  | { status: 'error'; studies: []; httpStatus: number | undefined }
  | { status: 'ready'; studies: ClinicalStudy[] };

const createClinicalStudySchema = z.object({
  name: z.string().min(1, 'Campo obbligatorio').max(200),
  description: z.string().max(2000).optional(),
});
type CreateClinicalStudyForm = z.infer<typeof createClinicalStudySchema>;

/**
 * The `/clinical-study/studies` drill-down root (Resolution R9): lists every Clinical Study of
 * the operating Organization and lets a study administrator create a new one — mirrors
 * `TenantsPage.tsx`'s list+create+`DataTable` shape. Reaches `/v1/clinical-study/studies` with NO
 * explicit `params` object (Resolution R2): this route carries no declared header requirement in
 * its generated type, so the ambient `api-client.ts` middleware attaches
 * `X-Active-Tenant-Id`/`X-Active-Organization-Id`.
 */
export function ClinicalStudiesPage() {
  const { t } = useTranslation(['clinical-study', 'errors']);
  const [state, setState] = useState<LoadState>({ status: 'loading', studies: [] });
  const [apiError, setApiError] = useState<string | null>(null);
  // The list route is `clinical-study:study:read` and the create route is `:manage`
  // (`clinical-studies.controller.ts`), so an investigator or CRO monitor reaches this page and
  // sees the whole list — but their create submission 403s. `canUse` fails closed, so the form is
  // hidden while the permission read is loading or errored, mirroring `ClinicalStudyDetailPage`,
  // which closed the same defect on the sibling screen on 2026-08-11.
  const callerPermissions = useCallerPermissions();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateClinicalStudyForm>({ resolver: zodResolver(createClinicalStudySchema) });
  const { pending, run } = usePendingWrite();

  const load = () => {
    setState({ status: 'loading', studies: [] });
    apiClient
      .GET('/v1/clinical-study/studies')
      .then(({ data, error, response }) => {
        // The status chooses the words (`readErrorKey`); it is never shown itself.
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error', studies: [], httpStatus: response.status });
          return;
        }
        setState({ status: 'ready', studies: data });
      })
      .catch(() => setState({ status: 'error', studies: [], httpStatus: undefined }));
  };

  useEffect(load, []);

  const onSubmit = async (values: CreateClinicalStudyForm) => {
    setApiError(null);
    // Mirrors RolesSection.tsx's/UsersSection.tsx's own established convention: an untouched
    // optional text `<Input>` submits an empty string, not `undefined` — `?? null` alone would
    // therefore never convert it to `null`.
    const description =
      values.description === '' || values.description === undefined ? null : values.description;
    const { data, error, response } = await apiClient.POST('/v1/clinical-study/studies', {
      body: { name: values.name, description },
    });

    if (error !== undefined || !response.ok || data === undefined) {
      setApiError(error?.detail ?? t('clinical-study:writeError'));
      return;
    }

    reset();
    setState((previous) =>
      previous.status === 'ready'
        ? { status: 'ready', studies: [data, ...previous.studies] }
        : previous,
    );
    toast.success(t('clinical-study:studies.createdToast'));
  };

  return (
    <div className="space-y-6">
      <h2 className="screen-title">{t('clinical-study:studies.title')}</h2>

      {canUse(callerPermissions, 'clinical-study:study:manage') && (
        <Card>
          <CardHeader>
            <CardTitle>{t('clinical-study:studies.createTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={(event) =>
                void handleSubmit((values) => run(() => onSubmit(values)))(event)
              }
            >
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="clinical-study-name">
                  {t('clinical-study:studies.nameLabel')}
                </label>
                <Input id="clinical-study-name" {...register('name')} />
                {errors.name && <p className="text-destructive text-sm">{errors.name.message}</p>}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="clinical-study-description">
                  {t('clinical-study:studies.descriptionLabel')}
                </label>
                <Input
                  id="clinical-study-description"
                  aria-describedby="clinical-study-description-hint"
                  {...register('description')}
                />
                <p id="clinical-study-description-hint" className="text-xs text-muted-foreground">
                  {t('clinical-study:studies.descriptionHint')}
                </p>
                {errors.description && (
                  <p className="text-destructive text-sm">{errors.description.message}</p>
                )}
              </div>
              <Button type="submit" disabled={pending}>
                {t('clinical-study:studies.createSubmit')}
              </Button>
              {apiError && <p className="text-destructive text-sm">{apiError}</p>}
            </form>
          </CardContent>
        </Card>
      )}

      {state.status === 'loading' && <TableSkeleton columns={4} />}
      {state.status === 'error' && (
        <div className="space-y-2">
          <p className="text-destructive flex items-center gap-2 text-sm">
            <CircleAlert className="size-5 shrink-0" aria-hidden="true" />
            {t(readErrorKey(state.httpStatus))}
          </p>
          <Button variant="outline" size="sm" onClick={load}>
            {t('clinical-study:studies.retry')}
          </Button>
        </div>
      )}
      {state.status === 'ready' && (
        <DataTable
          columns={[
            {
              accessorKey: 'name',
              header: t('clinical-study:studies.nameLabel'),
              cell: ({ row }) => (
                <Link to={`/clinical-study/studies/${row.original.id}`} className="underline">
                  {row.original.name}
                </Link>
              ),
            },
            {
              accessorKey: 'description',
              header: t('clinical-study:studies.descriptionLabel'),
            },
            {
              accessorKey: 'visitReady',
              header: t('clinical-study:studies.visitReadyLabel'),
              cell: ({ getValue }) =>
                getValue<boolean>()
                  ? t('clinical-study:studies.visitReadyYes')
                  : t('clinical-study:studies.visitReadyNo'),
            },
            {
              accessorKey: 'createdAt',
              header: t('clinical-study:studies.createdAtLabel'),
              cell: ({ getValue }) => formatDate(getValue<string>()),
            },
          ]}
          data={state.studies}
          emptyMessage={t('clinical-study:studies.emptyMessage')}
        />
      )}

      <Link to="/clinical-study/treatment-date-pool" className="text-sm underline">
        {t('clinical-study:studies.poolLink')}
      </Link>
    </div>
  );
}
